import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Grid, Loader, Dimmer } from 'semantic-ui-react';
import Swal from 'sweetalert2';
import { utils } from '../../helpers/utils';
import axiosInstance, { errorMessageForCode } from '../../helpers/axiosInstance';
import { CreatePasswordModel } from '../../types/UserDTO';
import logo from "../../assets/images/login-logo.png";

type FormErrorFields = {
  password: string;
  confirmPassword: string;
};

export const CreatePasswordForm: React.FC = () => {
  const navigate = useNavigate();

  const { userGuid } = useParams();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const [formValues, setFormValues] = useState<CreatePasswordModel>({} as CreatePasswordModel);
  const [formErrors, setFormErrors] = useState<FormErrorFields>({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    document.title = 'Create Password - Adjure';
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await axiosInstance.get(`/Auth/GetUserRegistration/${userGuid}`).then(({ data, status }) => {
        if (status === 200) {
          setEmail(data);
        }
        setIsLoading(false);
      })
        .catch((error) => {
          setIsLoading(false);
          if (error?.response?.data?.Message) {
            setErrorMessage(error.response.data.Message)
          } else {
            setErrorMessage(error.message);
          }
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    validate(false, name, value);
  };

  const validate = async (validateAll: boolean, fieldName: string | null = null, value: any | null = null): Promise<FormErrorFields> => {
    return await new Promise((resolve, reject) => {
      let errors: FormErrorFields = { ...formErrors };
      try {
        if (validateAll) {
          errors = {
            password: !formValues?.password ? 'Password is required' : (!utils.isValidPassword(formValues.password) ? 'Please provide a least 8 characters, one numeric character and one special character' : ''),
            confirmPassword: !formValues?.confirmPassword ? 'Confirm password is required' : (formValues?.password !== formValues?.confirmPassword) ? 'Password & confirm password should be same' : '',
          };
        } else {
          switch (fieldName) {
            case 'password':
              errors.password = !value ? 'Password is required' : (!utils.isValidPassword(value) ? 'Please provide a least 8 characters, one numeric character and one special character' : '');
              break;
            case 'confirmPassword':
              errors.confirmPassword = !value ? 'Confirm password is required' : (value !== formValues?.password) ? 'Password & confirm password should be same' : '';
              break;
            default:
              break;
          }
        }
        setFormErrors(errors);
        resolve(errors);
      } catch (error) {
        console.log(`Validation error`);
        reject(errors);
      }
    });
  }

  const handleSubmit = async () => {
    const errors = await validate(true);
    const hasErrors = Object.values(errors).some((error) => error);
    if (!hasErrors) {
      // handle form submission 
      let payload = {
        registrationCode: userGuid,
        password: formValues.password
      };
      Swal.fire({
        title: 'Are you sure you want to submit?',
        confirmButtonText: 'Submit',
        showConfirmButton: true,
        showDenyButton: true,
        denyButtonText: 'Cancel',
        denyButtonColor: '#657193'
      }).then((result) => {
        if (result.isConfirmed) {
          setIsLoading(true);
          axiosInstance.post(`/Auth/RegisterUser`, payload).then(({ data, status, statusText }) => {
            setIsLoading(false);
            if (status === 200) {
              Swal.fire({
                title: 'Login created',
                icon: 'success',
                html: `<div class="form-custom" style="text-align: left">
                    <p>Login created successfully. Click on the button below to login. Close the browser if you want to login at a later time.</p>
                  </div>`,
                confirmButtonText: 'Login',
                showConfirmButton: true,
                showDenyButton: false,
                allowOutsideClick: false,
                allowEscapeKey: false
              }).then((result) => {
                if (result.isConfirmed) {
                  navigate('/login');
                }
              });
            }
          })
            .catch((error) => {
              setIsLoading(false);
              Swal.fire({
                icon: 'error',
                title: 'Ope!',
                html: `<p>${error?.response?.data?.Message ? error.response.data.Message : error.message}</p><p>(${errorMessageForCode(error?.response?.status)})</p>`,
                confirmButtonText: 'OK',
              });
            });
        }
      });
    }
  };

  return (
    <Dimmer.Dimmable as="div" dimmed={isLoading}>
      <Dimmer active={isLoading} inverted={true}>
        <Loader>Loading</Loader>
      </Dimmer>
      <Grid textAlign="center" verticalAlign="middle" style={{ height: '100vh' }}>
        <Grid.Row>
          <Grid.Column>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <img src={logo} alt="Adjure CRM" />
              {errorMessage && (
                <>
                  <div className="ui error message">
                    <div className="content">
                      <div className="header">Error!</div>
                      <div>{errorMessage}</div>
                    </div>
                  </div>
                  <Grid style={{ marginBottom: '1rem' }}>
                    <Grid.Column>
                      <Button type="button" className='second button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', color: 'white', fontSize: '1.6rem' }} onClick={() => navigate('/login')}>
                        Back to login
                      </Button>
                    </Grid.Column>
                  </Grid>
                </>
              )}
              {!errorMessage && (
                <Form className='form-custom'>
                  <h2 style={{ marginTop: '1rem' }}>Create your password to access Adjure CRM</h2>
                  <div className="inline fields">
                    <div className="field">
                      <div className="field">
                        <label>Login</label>
                        <div className="ui input">{email}</div>
                      </div>
                    </div>
                  </div>
                  <p style={{ textAlign: 'left' }}>Your password must be minimum least 8 characters long and must include one numeric character and one special character</p>
                  <Form.Group inline>
                    <Form.Field style={{ textAlign: 'left' }}>
                      <Form.Input
                        type="password"
                        label="Password"
                        name="password"
                        value={formValues.password}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.password}
                        required
                      />
                      {formErrors?.password && <div className="error-message" style={{ color: 'red', textAlign: 'left' }}>{formErrors?.password}</div>}
                    </Form.Field>
                  </Form.Group>
                  <Form.Group inline>
                    <Form.Field>
                      <Form.Input
                        type="password"
                        label="Confirm Password"
                        name="confirmPassword"
                        value={formValues.confirmPassword}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        error={!!formErrors?.confirmPassword}
                        required
                      />
                      {formErrors?.confirmPassword && <div className="error-message" style={{ color: 'red', textAlign: 'left' }}>{formErrors?.confirmPassword}</div>}
                    </Form.Field>
                  </Form.Group>
                  <Button type="submit" onClick={handleSubmit} className='violet button' style={{ padding: '1.1rem 2.1rem', borderRadius: '0rem', fontSize: '1.6rem' }}>
                    Submit
                  </Button>
                </Form>
              )}
            </div>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </Dimmer.Dimmable >
  );
}

export default CreatePasswordForm;